import { queryKeys } from '@fidalia/api';
import { Alert, Button, Spinner } from '@fidalia/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { AuthLayout } from '@/features/auth/auth-layout';
import { tokenStore } from '@/lib/api';

/** Messages associés aux codes renvoyés par le backend. */
const MESSAGES: Record<string, string> = {
  compte_desactive: 'Cet accès a été désactivé. Contactez un administrateur de votre cabinet.',
  // Coupure décidée par le back-office : l'administrateur du cabinet n'y peut
  // rien, c'est le support Fidalia qu'il faut joindre.
  cabinet_desactive:
    'L’accès de votre cabinet a été désactivé par l’administrateur. Contactez le support.',
  email_non_verifie:
    'Cette adresse Google n’est pas vérifiée. Vérifiez-la chez Google, puis réessayez.',
  consentement_refuse: 'Connexion annulée.',
  state_invalide: 'La demande a expiré ou n’a pas pu être vérifiée. Réessayez.',
  echec: 'La connexion Google a échoué. Réessayez ou utilisez votre email.',
};

const searchSchema = z.object({ erreur: z.string().optional() });

export const Route = createFileRoute('/connexion/google')({
  validateSearch: searchSchema,
  component: RetourGooglePage,
});

/**
 * Atterrissage du flux OAuth. Le backend redirige ici avec, dans le
 * **fragment**, qui ne quitte jamais le navigateur, ni journaux serveur ni
 * en-tête `Referer` :
 *
 * - `#token=…` : compte connu, la session est ouverte ;
 * - `#inscription=…` : adresse inconnue, ticket signé pour finir l'inscription.
 */
function RetourGooglePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { erreur } = Route.useSearch();

  // Lu une seule fois au montage : l'effet efface le fragment juste après, une
  // seconde lecture ne trouverait plus rien.
  const [fragment] = useState(() => {
    if (erreur) return null;
    const params = new URLSearchParams(globalThis.location.hash.slice(1));
    return { token: params.get('token'), inscription: params.get('inscription') };
  });

  const reconnu = Boolean(fragment?.token || fragment?.inscription);

  // Dérivé du rendu plutôt que posé depuis l'effet : pas de rendu en cascade.
  const echec = erreur ? (MESSAGES[erreur] ?? MESSAGES.echec!) : reconnu ? null : MESSAGES.echec!;

  useEffect(() => {
    if (!fragment) return;

    // Le fragment est effacé de la barre d'adresse : ni le jeton ni le ticket
    // ne restent dans l'historique ou dans un lien partagé par mégarde.
    globalThis.history.replaceState(null, '', globalThis.location.pathname);

    if (fragment.token) {
      tokenStore.set(fragment.token);
      // La session change : tout cache antérieur est obsolète.
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
      void navigate({ to: '/', replace: true });
      return;
    }

    if (fragment.inscription) {
      // Le ticket transite par l'état du routeur, pas par l'URL : il ne
      // s'affiche nulle part et ne part pas dans un presse-papiers.
      void navigate({
        to: '/register',
        replace: true,
        state: { ticketGoogle: fragment.inscription },
      });
    }
  }, [fragment, navigate, queryClient]);

  if (!echec) {
    return (
      <div className="fixed inset-0 grid place-items-center bg-surface-page">
        <div className="flex flex-col items-center gap-3 text-content-muted">
          <Spinner className="size-6" />
          <p className="text-sm">Connexion en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <AuthLayout title="Connexion Google" subtitle="Nous n’avons pas pu vous connecter.">
      <Alert tone="danger">{echec}</Alert>

      <Link to="/login">
        <Button size="lg" fullWidth>
          Retour à la connexion
        </Button>
      </Link>
    </AuthLayout>
  );
}
